import { useContext, useEffect, useMemo } from 'react';
import DashboardsContext from 'contexts/DashboardsContext';
import { buildShowEntityAppContentTab } from 'navigation/appRoutes';
import isDefined from 'isDefined';
import getIdentifier from 'getIdentifier';
import useEntityParams from './useEntityParams';
import BoardsContext from '../../../contexts/BoardsContext';
import isPerformanceBoard from '../../../isPerformanceBoard';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import ReportsContext from '../../../contexts/ReportsContext';
import isDriverPerformanceBoard from '../../../isDriverPerformanceBoard';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import isCustomerLaneBoard from '../../../isCustomerLaneBoard';
import { useSearchParams } from 'react-router-dom';
import PopupEntityAppContext from '../../../contextProviders/SplashScreenProviders/MiscProviders/PopupEntityAppProvider/PopupEntityAppContext';
import ScorecardsContext from '../../../contexts/ScorecardsContext';
import programHasSubPeriods from 'contextProviders/BonusAppProvider/programHasSubPeriods';

const useTabs = ({ app }: { app: EntityDetails.App | undefined }) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { boards } = useContext(BoardsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { allReports } = useContext(ReportsContext);
  const { scorecards } = useContext(ScorecardsContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const popupEntityAppContext = useContext(PopupEntityAppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { tabId, tabType, value } = useEntityParams();

  const tabs = useMemo((): EntityDetails.ClientTab[] => {
    if (!app) {
      return [];
    }

    // Programmes using the sub-period pattern do their overrides through the board
    // slideout, not the full-screen driver overview — so drop the per-program
    // overview tab (and the combined bonus tab, which reuses that overview) for
    // them. Dropping here removes them from the screen, both sidebars and the nav
    // in one place (the list is filter(isDefined)-ed). Inert for other programmes.
    const appHasSubPeriodProgram = app.tabs.some((tab) => {
      if (tab.type !== 'driver-bonus-program-report') {
        return false;
      }
      return programHasSubPeriods(
        bonusPrograms.find((p) => p.id === tab.typeId),
      );
    });

    return app.tabs
      .map((tab) => {
        const v3Link = (() => {
          searchParams.set('tabType', tab.type);
          searchParams.set('tabId', tab.typeId);
          return `?${searchParams.toString()}`;
        })();
        const key = getIdentifier(`${tab.type}-${tab.typeId}`, true);
        const isSelected =
          tab.typeId === tabId &&
          (tabType === undefined || tab.type === tabType);
        const link = popupEntityAppContext
          ? v3Link
          : buildShowEntityAppContentTab({
              entityApp: app,
              value,
              tabId: tab.typeId,
              tabType: tab.type,
              portal: selectedPortal,
            });

        if (tab.type === 'dashboard') {
          const dashboard = allDashboards.find((d) => d.id === tab.typeId);
          if (!dashboard) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? dashboard.name,
            assetName: dashboard.name,
            label: tab.label,
            dashboard,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'board') {
          const board = boards.find((d) => d.id === tab.typeId);
          if (!board) {
            return undefined;
          }

          if (
            !(
              isPerformanceBoard(board) ||
              isDriverPerformanceBoard(board) ||
              isCustomerLaneBoard(board)
            )
          ) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? board.name,
            assetName: board.name,
            label: tab.label,
            board,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'report') {
          const report = allReports.find((r) => r.id === tab.typeId);
          if (!report) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? report.name,
            assetName: report.name,
            label: tab.label,
            report,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'scorecard') {
          const scorecard = scorecards.find((s) => s.id === tab.typeId);
          if (!scorecard) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? scorecard.title,
            assetName: scorecard.title,
            label: tab.label,
            scorecard,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'driver-bonus-program-report') {
          const bonusProgram = bonusPrograms.find((p) => p.id === tab.typeId);
          if (!bonusProgram) {
            return undefined;
          }

          // Sub-period programmes override via the board slideout — hide the
          // full-screen overview tab for them.
          if (programHasSubPeriods(bonusProgram)) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? bonusProgram.name,
            assetName: bonusProgram.name,
            label: tab.label,
            bonusProgram,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'driver-bonus-combined') {
          // The combined bonus tab reuses the full-screen overview, so hide it
          // when any of this entity app's programmes uses sub-periods.
          if (appHasSubPeriodProgram) {
            return undefined;
          }

          return {
            key,
            link,
            name: tab.label ?? 'Bonus',
            assetName: 'Bonus',
            label: tab.label,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else if (tab.type === 'driver-details-tab') {
          return {
            key,
            link,
            name: tab.label ?? 'Driver Details',
            assetName: 'Driver Details',
            label: tab.label,
            isSelected,
            type: tab.type,
            typeId: tab.typeId,
          };
        } else {
          return undefined;
        }
      })
      .filter(isDefined);
  }, [
    allDashboards,
    allReports,
    app,
    boards,
    bonusPrograms,
    popupEntityAppContext,
    scorecards,
    searchParams,
    selectedPortal,
    tabId,
    tabType,
    value,
  ]);

  const selectedTab = useMemo(() => {
    return tabs.find((t) => t.isSelected);
  }, [tabs]);

  useEffect(() => {
    if (!selectedTab && popupEntityAppContext && tabs.length > 0) {
      setSearchParams((current) => {
        current.set('tabType', tabs[0].type);
        current.set('tabId', tabs[0].typeId);
        return current;
      });
    }
  }, [popupEntityAppContext, selectedTab, setSearchParams, tabs]);

  return { tabs, selectedTab };
};

export default useTabs;
